import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Scrollbar } from "react-scrollbars-custom";
import { useNavigate, useParams } from "react-router-dom";

import ScrollBoxComponent from '../../ScrollBox/ScrollBox';
import TicketListItem from './TicketListItem/TicketListItem';
import "./TicketList.scss";
import { fetchAllTickets, setSelectedTicket } from "../../../../slices/TicketSlice";
export default function TicketList({ width = "100%", user }) {
  const tickets = useSelector((state) => state.tickets.ticketsList);
  const status = useSelector((state) => state.tickets.ticketStatus);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { ticketId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (ticketId && tickets.length === 0 && status !== "loading") {
      dispatch(fetchAllTickets());
    }
  }, [ticketId, tickets.length, status, dispatch]);

  useEffect(() => {
    if (status === "succeeded" && ticketId && tickets.length > 0) {
      const foundTicket = tickets.find(
        (ticket) => String(ticket.id) === String(ticketId)
      );

      if (foundTicket) {
        dispatch(setSelectedTicket(foundTicket));
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [status, ticketId, tickets, dispatch, navigate]);

  const visibleTickets = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const normalizeStatus = (value) =>
      (value || "")
        .toString()
        .toLowerCase()
        .replaceAll("_", " ")
        .trim();

    return [...(tickets || [])]
      .filter((ticket) => {
        const matchesTitle = !query || ticket.title?.toLowerCase().includes(query);
        const matchesStatus =
          statusFilter === "all" ||
          normalizeStatus(ticket.status) === normalizeStatus(statusFilter);
        return matchesTitle && matchesStatus;
      })
      .sort((a, b) => {
        const aClosed = a.status?.toLowerCase() === "closed";
        const bClosed = b.status?.toLowerCase() === "closed";
        if (aClosed !== bClosed) return aClosed ? 1 : -1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
  }, [tickets, searchTerm, statusFilter]);

  return (
    <div className="student-ticket-list">
      <div className="my-tickets-label">My Tickets</div>

      <div className="ticket-list-controls">
        <label className="ticket-search">
          <span className="sr-only">Search tickets by title</span>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by title..."
          />
        </label>

        <label className="status-filter">
          <span className="sr-only">Filter tickets by status</span>
          <select
            className={`status-filter-select filter-${statusFilter.replaceAll(" ", "-")}`}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option className="filter-all" value="all">All Statuses</option>
            <option className="filter-unassigned" value="unassigned">Unassigned</option>
            <option className="filter-in-progress" value="in progress">In Progress</option>
            <option className="filter-blocked" value="blocked">Blocked</option>
            <option className="filter-resolved" value="resolved">Resolved</option>
            <option className="filter-closed" value="closed">Closed</option>
          </select>
        </label>
      </div>

      <ScrollBoxComponent
        widthbox={width}
        user={user}
        className="ScrollBoxComponent"
        style={{ minHeight: "0", display: "block", width: "100%" }}
      >
        {status === "succeeded" && (
          <Scrollbar
            className="scrollbar"
            trackYProps={{ style: { width: 8 } }}
            noScrollX
          >
            {visibleTickets.length === 0 ? (
              <div className="no-tickets" data-testid="no-tickets-message">
                {tickets?.length ? "No matching tickets..." : "No tickets..."}
              </div>
            ) : (
              <div className="ticket-list" data-testid="ticket-list-items">
                {visibleTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="ticket-row"
                    data-status={(ticket.status || "unassigned")
                      .toLowerCase()
                      .replace(/\s+/g, "-")}
                  >
                    <TicketListItem
                      ticket={ticket}
                      data-testid={`ticket-list-item-${ticket.id}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </Scrollbar>
        )}
      </ScrollBoxComponent>
    </div>
  );
}
